import { ApiResponse } from '../types';
import { apiClient } from './apiClient';

// Report Types
export interface SalesReport {
  date: string;
  total_sales: number;
  total_transactions: number;
  profit: number;
  average_transaction_value: number;
}

export interface CustomerReport {
  customer_id: number;
  customer_name: string;
  total_purchases: number;
  total_amount: number;
  last_purchase_date: string;
  customer_type: 'retail' | 'home' | 'wholesale' | 'business' | 'regular' | 'vip' | 'walk-in';
  dues: number; // Changed from balance_amount to dues for consistency
}

export interface InventoryReport {
  product_id: number;
  product_name: string;
  brand: string;
  current_stock: number;
  minimum_stock_level: number;
  cost_price: number;
  selling_price: number;
  total_value: number;
  expiry_date?: string;
  status: 'in-stock' | 'low-stock' | 'out-of-stock' | 'expiring-soon';
}

export interface FinancialSummary {
  total_revenue: number;
  total_cost: number;
  gross_profit: number;
  profit_margin: number;
  tax_collected: number;
  net_profit: number;
}

export interface DuesReport {
  customer_id: number;
  customer_name: string;
  phone?: string;
  total_due: number;
  last_payment_date?: string;
  days_overdue: number;
  sales_count: number;
  customer_type?: string;
}

export interface TopProductReport {
  product_id: number;
  product_name: string;
  brand: string;
  quantity_sold: number;
  revenue: number;
  profit: number;
}

export interface ExportResult {
  filename: string;
  downloadUrl: string;
  expiresAt: string;
  reportType: string;
  format: string;
  generatedAt: string;
}

interface ReportFilters {
  startDate: string;
  endDate: string;
  customerType?: 'all' | 'retail' | 'home' | 'wholesale' | 'business' | 'regular' | 'vip';
}

class ReportsService {
  /**
   * Get financial summary report
   */
  async getFinancialSummary(filters: ReportFilters): Promise<FinancialSummary> {
    try {
      const response = await apiClient.get<ApiResponse<FinancialSummary>>(
        '/reports/financial-summary',
        {
          params: {
            startDate: filters.startDate,
            endDate: filters.endDate,
            customerType: filters.customerType,
          },
        }
      );

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch financial summary');
      }
    } catch (error: any) {
      console.error('Error fetching financial summary:', error);
      
      // Provide more specific error messages
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (status === 403) {
          throw new Error('You do not have permission to access financial reports.');
        } else if (status === 404) {
          throw new Error('Financial summary endpoint not found. Please contact support.');
        } else if (data?.message) {
          throw new Error(data.message);
        }
      } else if (error.request) {
        throw new Error('Cannot reach the server. Please check your connection.');
      }
      
      throw new Error(error.message || 'Failed to fetch financial summary');
    }
  }

  /**
   * Get sales report
   */
  async getSalesReport(
    period: 'daily' | 'weekly' | 'monthly',
    filters: ReportFilters
  ): Promise<SalesReport[]> {
    try {
      const response = await apiClient.get<ApiResponse<SalesReport[]>>(
        '/reports/sales',
        {
          params: {
            period,
            startDate: filters.startDate,
            endDate: filters.endDate,
          },
        }
      );

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch sales report');
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
      throw new Error('Failed to fetch sales report. Please check your connection and ensure the backend API is running.');
    }
  }

  /**
   * Get customer report
   */
  async getCustomerReport(filters: ReportFilters): Promise<CustomerReport[]> {
    try {
      const params: any = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      if (filters.customerType && filters.customerType !== 'all') {
        params.customerType = filters.customerType;
      }

      const response = await apiClient.get<ApiResponse<CustomerReport[]>>(
        '/reports/customers',
        { params }
      );

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch customer report');
      }
    } catch (error) {
      console.error('Error fetching customer report:', error);
      throw new Error('Failed to fetch customer report. Please check your connection and ensure the backend API is running.');
    }
  }

  /**
   * Get inventory report
   */
  async getInventoryReport(): Promise<InventoryReport[]> {
    try {
      const response = await apiClient.get<ApiResponse<InventoryReport[]>>('/reports/inventory');

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch inventory report');
      }
    } catch (error) {
      console.error('Error fetching inventory report:', error);
      throw new Error('Failed to fetch inventory report. Please check your connection and ensure the backend API is running.');
    }
  }

  /**
   * Get dues report
   */
  async getDuesReport(filters?: ReportFilters): Promise<DuesReport[]> {
    try {
      const params: any = {};
      if (filters) {
        if (filters.customerType && filters.customerType !== 'all') {
          params.customerType = filters.customerType;
        }
      }

      const response = await apiClient.get<ApiResponse<DuesReport[]>>(
        '/reports/dues',
        { params }
      );

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch dues report');
      }
    } catch (error: any) {
      console.error('Error fetching dues report:', error);
      
      // Provide more specific error messages
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (status === 403) {
          throw new Error('You do not have permission to access dues reports.');
        } else if (status === 404) {
          throw new Error('Dues report endpoint not found. Please contact support.');
        } else if (data?.message) {
          throw new Error(data.message);
        }
      } else if (error.request) {
        throw new Error('Cannot reach the server. Please check your connection.');
      }
      
      throw new Error(error.message || 'Failed to fetch dues report');
    }
  }

  /**
   * Get top products report
   */
  async getTopProducts(limit: number = 10, filters?: ReportFilters): Promise<TopProductReport[]> {
    try {
      const params: any = { limit };
      
      if (filters) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }

      const response = await apiClient.get<ApiResponse<TopProductReport[]>>(
        '/reports/top-products',
        { params }
      );

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch top products report');
      }
    } catch (error: any) {
      console.error('Error fetching top products report:', error);
      
      // Provide more specific error messages
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (status === 403) {
          throw new Error('You do not have permission to access product reports.');
        } else if (status === 404) {
          throw new Error('Top products report endpoint not found. Please contact support.');
        } else if (data?.message) {
          throw new Error(data.message);
        }
      } else if (error.request) {
        throw new Error('Cannot reach the server. Please check your connection.');
      }
      
      throw new Error(error.message || 'Failed to fetch top products report');
    }
  }
  
  /**
   * Export report
   */
  async exportReport(
    reportType: 'sales' | 'financial' | 'customers' | 'inventory' | 'dues',
    format: 'pdf' | 'csv',
    filters: ReportFilters
  ): Promise<ExportResult> {
    try {
      const requestData = {
        reportType,
        format,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customerType: filters.customerType || 'all',
      };

      console.log('Export request data:', requestData);

      const response = await apiClient.post<ApiResponse<ExportResult>>(
        '/reports/export',
        requestData
      );

      console.log('Export response:', response);

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to export report');
      }
    } catch (error: any) {
      console.error('Error exporting report:', error);
      console.error('Error response:', error.response?.data);
      throw new Error('Failed to export report. Please check your connection and ensure the backend API is running.');
    }
  }

  /**
   * Export individual customer report with their transaction history and dues
   */
  async exportCustomerReport(
    customerId: number,
    format: 'pdf' | 'csv',
    filters?: ReportFilters
  ): Promise<ExportResult> {
    try {
      const requestData: any = {
        format,
      };

      if (filters) {
        requestData.startDate = filters.startDate;
        requestData.endDate = filters.endDate;
      }

      console.log('Export customer report request:', requestData);

      const response = await apiClient.post<ApiResponse<ExportResult>>(
        `/customers/${customerId}/export-report`,
        requestData
      );

      console.log('Export customer report response:', response);

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to export customer report');
      }
    } catch (error: any) {
      console.error('Error exporting customer report:', error);
      console.error('Error response:', error.response?.data);
      throw new Error('Failed to export customer report. Please check your connection and ensure the backend API is running.');
    }
  }
}

// Export singleton instance
export const reportsService = new ReportsService();
export default reportsService;
